import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Container, Paper, Typography, Grid, Button, TextField, Avatar,
  Divider, List, ListItem, ListItemText, Chip, Card, CardContent,
  Alert, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import FavoriteIcon from '@mui/icons-material/Favorite';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import EditIcon from '@mui/icons-material/Edit';
import LogoutIcon from '@mui/icons-material/Logout';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HistoryIcon from '@mui/icons-material/History';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import HomeIcon from '@mui/icons-material/Home';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const MemberCenter = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated, logout, updateProfile, loading: authLoading } = useAuth();

  // 從 URL 參數讀取初始 tab
  const initialTab = parseInt(searchParams.get('tab') || '0', 10);
  const [tabValue, setTabValue] = useState(initialTab);
  const [viewedProducts, setViewedProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [profileForm, setProfileForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: ''
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [selectedOrder, setSelectedOrder] = useState(null);

  // 未登入時導向登入頁
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { state: { from: { pathname: '/member' } } });
    }
  }, [isAuthenticated, authLoading, navigate]);

  // 載入會員資料
  useEffect(() => {
    if (user) {
      setProfileForm({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        address: user.address || ''
      });
    }
  }, [user]);

  // 載入訂單
  useEffect(() => {
    if (isAuthenticated && tabValue === 1) {
      fetchOrders();
    }
  }, [isAuthenticated, tabValue]);

  const fetchOrders = async () => {
    setLoadingOrders(true);
    try {
      const response = await api.get('/orders/my-orders');
      setOrders(response.data);
    } catch (error) {
      // 開發環境使用模擬資料
      setOrders([
        {
          id: 'ORD-2024120301',
          createdAt: '2024-12-03T10:30:00Z',
          status: 'delivered',
          statusText: '已送達',
          total: 599,
          shippingFee: 0,
          paymentMethod: '超商取貨付款',
          shippingMethod: '7-11 超商取貨',
          trackingNumber: 'CVS123456789',
          items: [
            { id: 1, name: '可愛動物造型筆', quantity: 2, price: 29 },
            { id: 2, name: '攜帶式噴霧瓶', quantity: 3, price: 5 }
          ]
        },
        {
          id: 'ORD-2024120102',
          createdAt: '2024-12-01T15:20:00Z',
          status: 'shipping',
          statusText: '配送中',
          total: 459,
          shippingFee: 60,
          paymentMethod: 'LINE Pay',
          shippingMethod: '全家超商取貨',
          trackingNumber: 'CVS987654321',
          items: [
            { id: 3, name: '環保購物袋', quantity: 1, price: 99 },
            { id: 4, name: '文具組合包', quantity: 2, price: 150 }
          ]
        },
        {
          id: 'ORD-2024112801',
          createdAt: '2024-11-28T09:00:00Z',
          status: 'pending',
          statusText: '待付款',
          total: 299,
          shippingFee: 60,
          paymentMethod: '超商代碼繳費',
          shippingMethod: '7-11 超商取貨',
          trackingNumber: null,
          items: [
            { id: 5, name: '創意收納盒', quantity: 1, price: 239 }
          ]
        }
      ]);
    }
    setLoadingOrders(false);
  };

  // 儲存個人資料
  const handleSaveProfile = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    const result = await updateProfile(profileForm);

    if (result.success) {
      setMessage({ type: 'success', text: '個人資料已更新' });
      setEditMode(false);
    } else {
      setMessage({ type: 'error', text: result.error || '更新失敗' });
    }

    setSaving(false);
  };

  // 登出
  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // 取得訂單狀態顏色
  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      paid: 'info',
      processing: 'info',
      shipping: 'primary',
      delivered: 'success',
      cancelled: 'error'
    };
    return colors[status] || 'default';
  };

  // 格式化日期
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Grid container spacing={3}>
        {/* 左側邊欄 - 會員資訊 */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Avatar
              src={user?.avatar}
              sx={{ width: 80, height: 80, mx: 'auto', mb: 2, bgcolor: 'primary.main' }}
            >
              {user?.name?.charAt(0) || <PersonIcon />}
            </Avatar>
            <Typography variant="h6" fontWeight="bold">
              {user?.name || '會員'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {user?.phone || user?.email}
            </Typography>
            <Chip
              label={user?.provider === 'google' ? 'Google 帳號' : user?.provider === 'line' ? 'LINE 帳號' : '手機會員'}
              size="small"
              color="primary"
              variant="outlined"
            />

            <Divider sx={{ my: 2 }} />

            <List dense>
              <ListItem button selected={tabValue === 0} onClick={() => setTabValue(0)}>
                <PersonIcon sx={{ mr: 2 }} color="action" />
                <ListItemText primary="個人資料" />
              </ListItem>
              <ListItem button selected={tabValue === 1} onClick={() => setTabValue(1)}>
                <ShoppingBagIcon sx={{ mr: 2 }} color="action" />
                <ListItemText primary="我的訂單" />
              </ListItem>
              <ListItem button selected={tabValue === 2} onClick={() => setTabValue(2)}>
                <FavoriteIcon sx={{ mr: 2 }} color="action" />
                <ListItemText primary="收藏清單" />
              </ListItem>
              <ListItem button selected={tabValue === 3} onClick={() => setTabValue(3)}>
                <LocalShippingIcon sx={{ mr: 2 }} color="action" />
                <ListItemText primary="常用地址" />
              </ListItem>
              <ListItem button selected={tabValue === 4} onClick={() => setTabValue(4)}>
                <HistoryIcon sx={{ mr: 2 }} color="action" />
                <ListItemText primary="瀏覽紀錄" />
              </ListItem>
            </List>

            <Divider sx={{ my: 2 }} />

            <Button
              fullWidth
              variant="outlined"
              color="error"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
            >
              登出
            </Button>
          </Paper>
        </Grid>

        {/* 右側內容區 */}
        <Grid item xs={12} md={9}>
          {/* 個人資料 */}
          {tabValue === 0 && (
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight="bold">
                  個人資料
                </Typography>
                {!editMode ? (
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => setEditMode(true)}
                  >
                    編輯
                  </Button>
                ) : (
                  <Box>
                    <Button onClick={() => setEditMode(false)} sx={{ mr: 1 }}>
                      取消
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleSaveProfile}
                      disabled={saving}
                    >
                      {saving ? <CircularProgress size={20} /> : '儲存'}
                    </Button>
                  </Box>
                )}
              </Box>

              {message.text && (
                <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage({ type: '', text: '' })}>
                  {message.text}
                </Alert>
              )}

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="姓名"
                    value={profileForm.name}
                    onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                    disabled={!editMode}
                    InputProps={{
                      startAdornment: <PersonIcon color="action" sx={{ mr: 1 }} />
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="手機號碼"
                    value={profileForm.phone}
                    onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                    disabled={!editMode || user?.provider === 'phone'}
                    InputProps={{
                      startAdornment: <PhoneIcon color="action" sx={{ mr: 1 }} />
                    }}
                    helperText={user?.provider === 'phone' ? '手機號碼為登入帳號，無法修改' : ''}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="電子郵件"
                    value={profileForm.email}
                    onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                    disabled={!editMode || user?.provider === 'google'}
                    InputProps={{
                      startAdornment: <EmailIcon color="action" sx={{ mr: 1 }} />
                    }}
                    helperText={user?.provider === 'google' ? 'Google 帳號，無法修改' : ''}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="預設收件地址"
                    value={profileForm.address}
                    onChange={(e) => setProfileForm({ ...profileForm, address: e.target.value })}
                    disabled={!editMode}
                    multiline
                    rows={2}
                    InputProps={{
                      startAdornment: <HomeIcon color="action" sx={{ mr: 1, alignSelf: 'flex-start', mt: 1 }} />
                    }}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                帳號安全
              </Typography>
              <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  登入方式：{user?.provider === 'google' ? 'Google 帳號' : user?.provider === 'line' ? 'LINE 帳號' : '手機號碼'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  註冊時間：{user?.createdAt ? formatDate(user.createdAt) : '未知'}
                </Typography>
              </Box>
            </Paper>
          )}

          {/* 我的訂單 */}
          {tabValue === 1 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                我的訂單
              </Typography>

              {loadingOrders ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                  <CircularProgress />
                </Box>
              ) : orders.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 5 }}>
                  <ShoppingBagIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                  <Typography color="text.secondary">您還沒有訂單</Typography>
                  <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/products')}>
                    開始購物
                  </Button>
                </Box>
              ) : (
                <Box>
                  {orders.map((order) => (
                    <Card key={order.id} variant="outlined" sx={{ mb: 2 }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Box>
                            <Typography variant="subtitle1" fontWeight="bold">
                              訂單編號：{order.id}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {formatDate(order.createdAt)}
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Chip
                              label={order.statusText}
                              color={getStatusColor(order.status)}
                              size="small"
                            />
                            <Typography variant="h6" color="error" sx={{ mt: 1 }}>
                              ${order.total}
                            </Typography>
                          </Box>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              付款方式：{order.paymentMethod}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              配送方式：{order.shippingMethod}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            {order.trackingNumber && (
                              <Typography variant="body2" color="text.secondary">
                                物流單號：{order.trackingNumber}
                              </Typography>
                            )}
                            <Typography variant="body2" color="text.secondary">
                              商品數量：{order.items.reduce((sum, item) => sum + item.quantity, 0)} 件
                            </Typography>
                          </Grid>
                        </Grid>

                        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<VisibilityIcon />}
                            onClick={() => setSelectedOrder(order)}
                          >
                            查看詳情
                          </Button>
                          {order.status === 'shipping' && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              startIcon={<LocalShippingIcon />}
                            >
                              追蹤物流
                            </Button>
                          )}
                          {order.status === 'pending' && (
                            <Button
                              size="small"
                              variant="contained"
                              color="warning"
                            >
                              立即付款
                            </Button>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </Paper>
          )}

          {/* 收藏清單 */}
          {tabValue === 2 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                收藏清單
              </Typography>
              <Box sx={{ textAlign: 'center', py: 5 }}>
                <FavoriteIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                <Typography color="text.secondary">您還沒有收藏商品</Typography>
                <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/products')}>
                  探索商品
                </Button>
              </Box>
            </Paper>
          )}

          {/* 常用地址 */}
          {tabValue === 3 && (
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight="bold">
                  常用地址
                </Typography>
                <Button variant="outlined" startIcon={<EditIcon />}>
                  新增地址
                </Button>
              </Box>

              <Alert severity="info" sx={{ mb: 3 }}>
                您可以儲存常用的收件地址與超商門市，結帳時更方便。
              </Alert>

              {profileForm.address ? (
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Box>
                        <Chip label="預設地址" size="small" color="primary" sx={{ mb: 1 }} />
                        <Typography variant="subtitle1" fontWeight="bold">
                          {profileForm.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {profileForm.phone}
                        </Typography>
                        <Typography variant="body2">
                          {profileForm.address}
                        </Typography>
                      </Box>
                      <IconButton onClick={() => setTabValue(0)}>
                        <EditIcon />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              ) : (
                <Box sx={{ textAlign: 'center', py: 5 }}>
                  <HomeIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                  <Typography color="text.secondary">您還沒有設定地址</Typography>
                  <Button variant="contained" sx={{ mt: 2 }} onClick={() => { setTabValue(0); setEditMode(true); }}>
                    新增地址
                  </Button>
                </Box>
              )}
            </Paper>
          )}

          {/* 瀏覽紀錄 */}
          {tabValue === 4 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                瀏覽紀錄
              </Typography>

              <RecentlyViewedProducts navigate={navigate} />
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* 訂單詳情 Dialog */}
      <Dialog
        open={Boolean(selectedOrder)}
        onClose={() => setSelectedOrder(null)}
        maxWidth="md"
        fullWidth
      >
        {selectedOrder && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight="bold">
                  訂單詳情
                </Typography>
                <Chip label={selectedOrder.statusText} color={getStatusColor(selectedOrder.status)} />
              </Box>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                訂單編號：{selectedOrder.id}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                訂購時間：{formatDate(selectedOrder.createdAt)}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                商品明細
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>商品名稱</TableCell>
                      <TableCell align="center">數量</TableCell>
                      <TableCell align="right">單價</TableCell>
                      <TableCell align="right">小計</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedOrder.items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.name}</TableCell>
                        <TableCell align="center">{item.quantity}</TableCell>
                        <TableCell align="right">${item.price}</TableCell>
                        <TableCell align="right">${item.price * item.quantity}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={3} align="right">運費</TableCell>
                      <TableCell align="right">
                        {selectedOrder.shippingFee > 0 ? `$${selectedOrder.shippingFee}` : '免運費'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={3} align="right">
                        <Typography fontWeight="bold">訂單總計</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="bold" color="error">${selectedOrder.total}</Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" fontWeight="bold">付款資訊</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedOrder.paymentMethod}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" fontWeight="bold">配送資訊</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedOrder.shippingMethod}
                  </Typography>
                  {selectedOrder.trackingNumber && (
                    <Typography variant="body2" color="primary">
                      物流單號：{selectedOrder.trackingNumber}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedOrder(null)}>關閉</Button>
              {selectedOrder.status === 'shipping' && (
                <Button variant="contained" startIcon={<LocalShippingIcon />}>
                  追蹤物流
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
};

// 瀏覽紀錄組件
const RecentlyViewedProducts = ({ navigate }) => {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    // 從 localStorage 讀取瀏覽紀錄
    const viewedHistory = JSON.parse(localStorage.getItem('viewedHistory') || '[]');
    setProducts(viewedHistory);
  }, []);

  const clearHistory = () => {
    localStorage.removeItem('viewedHistory');
    setProducts([]);
  };

  if (products.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 5 }}>
        <HistoryIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
        <Typography color="text.secondary">還沒有瀏覽紀錄</Typography>
        <Button
          variant="contained"
          sx={{ mt: 2 }}
          onClick={() => navigate('/products')}
        >
          去逛逛
        </Button>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button size="small" color="error" onClick={clearHistory}>
          清除紀錄
        </Button>
      </Box>
      <Grid container spacing={2}>
        {products.map((product) => (
          <Grid item xs={6} sm={4} md={3} key={product.id}>
            <Card
              sx={{
                cursor: 'pointer',
                '&:hover': { boxShadow: 3 }
              }}
              onClick={() => navigate(`/product/${product.id}`)}
            >
              <Box
                component="img"
                src={product.image || '/placeholder.png'}
                alt={product.name}
                sx={{
                  width: '100%',
                  aspectRatio: '1',
                  objectFit: 'cover'
                }}
              />
              <CardContent sx={{ p: 1.5 }}>
                <Typography
                  variant="body2"
                  noWrap
                  title={product.name}
                >
                  {product.name}
                </Typography>
                <Typography variant="subtitle2" color="error" fontWeight="bold">
                  ${product.price}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(product.viewedAt).toLocaleDateString('zh-TW')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </>
  );
};

export default MemberCenter;
